import React from "react";
import "./joyride.css";
import MapIcon from "material-ui/svg-icons/maps/place";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, DatePicker } from "material-ui-pickers";
import SelectField from "material-ui/SelectField";
import MenuItem1 from "material-ui/MenuItem";
import FlatButton from "material-ui/FlatButton";
import moment from "moment";
import Checkbox from "material-ui/Checkbox";
import PropTypes from "prop-types";
import { Button, Popover } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import FareTiers from "../../components/FareTiers";

/**
 * Formats the date range for a path, using effectiveFrom/effectiveTo dates and removing offDates
 * Always falls back to backward compatible startDate/endDate format if anything goes wrong
 * @param {Object} path - The path object containing schedules with effectiveFromDate, effectiveToDate, and offDates
 * @returns {string} - Formatted date range string
 */
const formatDateRange = (path) => {
  // Backward compatible fallback function
  const fallbackFormat = () => {
    const pathStart = moment(path.startDate);
    const pathEnd = moment(path.endDate);
    if (pathStart.isValid() && pathEnd.isValid()) {
      return `${pathStart.format("Do MMM YYYY")} - ${pathEnd.format(
        "Do MMM YYYY"
      )}`;
    }
    return `${moment(path.startDate).format("Do MMM YYYY")} - ${moment(
      path.endDate
    ).format("Do MMM YYYY")}`;
  };

  // Always fallback if startDate/endDate are not available
  if (!path.startDate || !path.endDate) {
    return fallbackFormat();
  }

  // If no schedules, fall back to startDate/endDate (backward compatibility)
  if (
    !path.schedules ||
    !Array.isArray(path.schedules) ||
    path.schedules.length === 0
  ) {
    return fallbackFormat();
  }

  try {
    // Find the earliest effectiveFromDate and latest effectiveToDate from all schedules
    let earliestDate = null;
    let latestDate = null;

    path.schedules.forEach((schedule) => {
      if (schedule && schedule.effectiveFromDate && schedule.effectiveToDate) {
        const fromDate = moment(schedule.effectiveFromDate);
        const toDate = moment(schedule.effectiveToDate);

        if (fromDate.isValid() && toDate.isValid()) {
          if (!earliestDate || fromDate.isBefore(earliestDate)) {
            earliestDate = fromDate;
          }
          if (!latestDate || toDate.isAfter(latestDate)) {
            latestDate = toDate;
          }
        }
      }
    });

    // If no valid dates found from schedules, fall back to startDate/endDate
    if (!earliestDate || !latestDate) {
      return fallbackFormat();
    }

    // Collect all offDates from all schedules
    const allOffDatesSet = new Set();
    path.schedules.forEach((schedule) => {
      if (
        schedule &&
        schedule.offDates &&
        Array.isArray(schedule.offDates) &&
        schedule.offDates.length > 0
      ) {
        schedule.offDates.forEach((dateStr) => {
          const offDate = moment(dateStr, "MM/DD/YYYY");
          if (offDate.isValid()) {
            allOffDatesSet.add(offDate.format("YYYY-MM-DD"));
          }
        });
      }
    });

    // Generate all dates in the range and filter out offDates
    const availableDates = [];
    const currentDate = moment(earliestDate);
    while (currentDate.isSameOrBefore(latestDate)) {
      const dateKey = currentDate.format("YYYY-MM-DD");
      if (!allOffDatesSet.has(dateKey)) {
        availableDates.push(moment(currentDate).clone());
      }
      currentDate.add(1, "day");
    }

    // If no available dates, fallback to startDate/endDate
    if (availableDates.length === 0) {
      return fallbackFormat();
    }

    // Group consecutive dates into ranges
    const dateRanges = [];
    let rangeStart = availableDates[0];
    let rangeEnd = availableDates[0];

    for (let i = 1; i < availableDates.length; i++) {
      const currentDate = availableDates[i];
      const prevDate = availableDates[i - 1];

      // Check if current date is consecutive (next day)
      if (currentDate.diff(prevDate, "days") === 1) {
        rangeEnd = currentDate;
      } else {
        // Save the current range and start a new one
        dateRanges.push({ start: moment(rangeStart), end: moment(rangeEnd) });
        rangeStart = currentDate;
        rangeEnd = currentDate;
      }
    }
    // Don't forget the last range
    dateRanges.push({ start: moment(rangeStart), end: moment(rangeEnd) });

    // Format the ranges
    const formattedRanges = dateRanges.map((range) => {
      if (range.start.isSame(range.end, "day")) {
        // Single date: "25th December"
        return range.start.format("Do MMMM YYYY");
      } else {
        // Date range: "21st-22nd Dec" or "26th Dec - 6th Jan"
        const startFormatted = range.start.format("Do MMM YYYY");
        const endFormatted = range.end.format("Do MMM YYYY");

        // If same month and year, use shorter format: "21st-22nd Dec"
        if (
          range.start.month() === range.end.month() &&
          range.start.year() === range.end.year()
        ) {
          const startDay = range.start.format("Do");
          const endDay = range.end.format("Do");
          const monthYear = range.start.format("MMM YYYY");
          return `${startDay}-${endDay} ${monthYear}`;
        } else {
          // Different month/year: "26th Dec - 6th Jan"
          return `${startFormatted} - ${endFormatted}`;
        }
      }
    });

    // Join with commas
    return formattedRanges.join(", ");
  } catch (error) {
    // If any error occurs, always fallback to backward compatible format
    console.error("Error formatting date range, falling back:", error);
    return fallbackFormat();
  }
};

/**
 * Collapses a schedule's priceLineItems into one total per age slab, so the
 * card can show every age fare before the user has picked a date. Returns null
 * when the schedules carry no age-based pricing, in which case no fare line is
 * rendered at all.
 * @param {Object} path - Path object carrying the schedules
 * @returns {Array|null} - Slabs sorted highest fare first
 */
const getFareSlabs = (path) => {
  const schedules = (path && path.schedules) || [];
  const schedule = schedules.find(
    (s) =>
      Array.isArray(s.priceLineItems) &&
      s.priceLineItems.some(
        (i) => Array.isArray(i.ageBasedPrices) && i.ageBasedPrices.length > 0
      )
  );

  if (!schedule) {
    return null;
  }

  const slabs = new Map();
  // Line items without age slabs are charged to every passenger, so they get
  // added on to each slab total.
  let flatTotal = 0;

  schedule.priceLineItems.forEach((item) => {
    if (Array.isArray(item.ageBasedPrices) && item.ageBasedPrices.length > 0) {
      item.ageBasedPrices.forEach((agePrice) => {
        const key = `${agePrice.minAge}-${agePrice.maxAge}`;
        const existing = slabs.get(key);
        slabs.set(key, {
          minAge: agePrice.minAge,
          maxAge: agePrice.maxAge,
          total: (existing ? existing.total : 0) + (agePrice.price || 0),
        });
      });
    } else if (item.price !== undefined && item.price !== null) {
      flatTotal += item.price;
    }
  });

  const list = Array.from(slabs.values())
    .map((slab) => ({ ...slab, total: slab.total + flatTotal }))
    .sort((a, b) => b.total - a.total);

  if (list.length === 0) {
    return null;
  }

  return list;
};

/**
 * Turns an age slab into the traveller type plus the age qualifier the fare
 * tiers render as a muted suffix.
 */
const toFareTier = (slab) => {
  const min = slab.minAge != null ? slab.minAge : 0;
  const max = slab.maxAge;

  if (max == null || max >= 100) {
    return { label: "Adult", ageText: `${min} yrs & above`, amount: slab.total };
  }
  if (min === 0) {
    return { label: "Child", ageText: `${max} yrs & below`, amount: slab.total };
  }
  return { label: `${min}-${max} yrs`, amount: slab.total };
};

const JoyRideForm = ({
  isLoading,
  path,
  index,
  selected,
  handleDateChange,
  handleScheduleChange,
  handleBookAction,
  availableSeats,
  payableAmount,
  error,
  tncChecks,
  onChangeTncCheck,
  onClickTnc,
  getSlotDescription,
}) => {
  const fareSlabs = getFareSlabs(path);
  const fareTiers = fareSlabs && fareSlabs.map(toFareTier);

  return (
    <div
      className="path-wrapper"
      key={path._id}
      style={{
        position: "relative",
        border: "1px solid #ccc",
        background: "white",
        display: "flex",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          background: "#ccc",
          zIndex: 2,
          opacity: 0.5,
          display: isLoading ? "block" : "none",
        }}
      >
        <div className="lds-roller">
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
      </div>
      <div
        className="details"
        style={{
          width: "40%",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          height: "100%",
        }}
      >
        <h3
          style={{
            textAlign: "left",
            margin: 0,
            padding: 0,
            paddingTop: "5px",
            paddingBottom: "8px",
            color: "#005299",
            fontSize: "24px",
            fontWeight: "bold",
          }}
        >
          {path.name}
        </h3>
        {path.locationLink && path.locationLink.trim() !== "" && (
          <div>
            <a
              href={path.locationLink}
              target="blank"
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <MapIcon style={{ height: "14px", width: "14px" }} />{" "}
              <span>Show Helipad on Map</span>
            </a>
          </div>
        )}
        <h4
          style={{
            paddingTop: "5px",
            paddingBottom: "5px",
          }}
        >
          <b style={{ fontSize: "18px" }}>{formatDateRange(path)}</b>
        </h4>
        <h5
          style={{
            fontSize: "14px",
            lineHeight: "20px",
            paddingTop: "5px",
            paddingBottom: "5px",
          }}
          dangerouslySetInnerHTML={{
            __html: path.description,
          }}
        ></h5>
        <div
          style={{
            textAlign: "right",
            marginTop: "auto",
          }}
        >
          <FlatButton
            label="Terms Of Service"
            disableTouchRipple
            onClick={onClickTnc}
          />
        </div>
      </div>
      <div
        className="seat-availability"
        style={{
          width: "60%",
          display: "inline-block",
          padding: "10px",
          paddingLeft: "20px",
          verticalAlign: "middle",
          position: "relative",
        }}
      >
        <div className="form-wrapper">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              margin="normal"
              label="Travel Date"
              onChange={handleDateChange}
              style={{
                height: "56px",
                margin: 0,
                marginRight: "10px",
                cursor: "pointer",
                verticalAlign: "text-bottom",
              }}
              value={selected.date[index] ? selected.date[index] : null}
              maxDate={new Date(path.endDate)}
              minDate={
                new Date(path.startDate) >= new Date()
                  ? new Date(path.startDate)
                  : new Date()
              }
              shouldDisableDate={(date) => {
                // Check if date is disabled by any schedule in this path
                return path.schedules.some((schedule) => {
                  const dateStr = moment(date).format("MM/DD/YYYY");
                  const dayOfWeek = moment(date).day();

                  // Check offDates
                  if (
                    schedule.offDates &&
                    schedule.offDates.includes(dateStr)
                  ) {
                    return true;
                  }

                  // Check offDaysOfTheWeek
                  if (
                    schedule.offDaysOfTheWeek &&
                    schedule.offDaysOfTheWeek.includes(dayOfWeek)
                  ) {
                    return true;
                  }

                  return false;
                });
              }}
            />
          </MuiPickersUtilsProvider>
          <SelectField
            floatingLabelText="Time Slot"
            className="time-slot-select"
            value={selected.schedule[index] ? selected.schedule[index]._id : 0}
            disabled={selected.date[index] ? false : true}
            style={{
              border: "none",
              width: "300px",
            }}
            onChange={handleScheduleChange}
          >
            <MenuItem1
              value={0}
              primaryText={"Select a Time Slot"}
              disabled={true}
            />
            {path.schedules
              .filter((s) => {
                if (!selected.date[index]) {
                  return false;
                }

                const selectedDate = moment(selected.date[index]).set({
                  hour: 0,
                  milliseconds: 0,
                  minute: 0,
                  second: 0,
                });

                const dateStr = selectedDate.format("MM/DD/YYYY");
                const dayOfWeek = selectedDate.day();

                // Check date range
                const isInDateRange =
                  selectedDate.diff(moment(s.effectiveFromDate)) >= 0 &&
                  selectedDate.diff(moment(s.effectiveToDate)) <= 0;

                if (!isInDateRange) {
                  return false;
                }

                // Check offDates
                if (s.offDates && s.offDates.includes(dateStr)) {
                  return false;
                }

                // Check offDaysOfTheWeek
                if (
                  s.offDaysOfTheWeek &&
                  s.offDaysOfTheWeek.includes(dayOfWeek)
                ) {
                  return false;
                }

                return true;
              })
              .map((s) => (
                <MenuItem1 key={s._id} value={s._id} primaryText={s.name} />
              ))}
          </SelectField>
        </div>
        {selected.schedule[index] && (
          <div>{getSlotDescription(selected.schedule[index])}</div>
        )}
        <div>
          {selected.schedule[index] &&
          availableSeats[index] === 0 &&
          !error[index] ? (
            <div
              className="available-seats"
              style={{
                display: "inline-block",
                padding: "5px",
                paddingLeft: 0,
                paddingRight: "15px",
                color: "black",
              }}
            >
              <b>
                <span
                  style={{
                    fontSize: "12px",
                    padding: "4px 4px 4px 0",
                    color: "red",
                    fontWeight: "600",
                    minHeight: "20px",
                  }}
                >
                  {"No seats available"}
                </span>
              </b>
            </div>
          ) : (
            ""
          )}
          <div
            className="payable-amount"
            style={{
              display: "inline-block",
              padding: "5px 0",
              fontSize: "18px",
            }}
          >
            {payableAmount[index] && availableSeats[index] > 0 ? (
              <b style={{ fontSize: "16px" }}>
                Per seat charges :{" "}
                {(() => {
                  // Check if early bird discount is active
                  const schedule = selected.schedule[index];
                  const earlyBirdDiscount = schedule?.earlyBirdDiscount;

                  // Check if discount is enabled and current date is before cutoff date
                  const isEarlyBirdActive =
                    earlyBirdDiscount?.enabled === true &&
                    earlyBirdDiscount?.bookingCutoffDate &&
                    moment().isSameOrBefore(
                      moment(earlyBirdDiscount.bookingCutoffDate)
                    );

                  const originalPrice = earlyBirdDiscount?.originalPrice;

                  const tooltipContent = isEarlyBirdActive ? (
                    <div style={{ padding: "4px 0" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          marginBottom: "4px",
                          color: "#d97706",
                        }}
                      >
                        Early Bird Offer
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        Flat Rs {earlyBirdDiscount?.discountAmount || 500} off
                        valid until{" "}
                        {moment(earlyBirdDiscount.bookingCutoffDate).format(
                          "Do MMM YYYY"
                        )}
                      </div>
                    </div>
                  ) : null;

                  if (isEarlyBirdActive && originalPrice) {
                    return (
                      <span
                        style={{
                          fontSize: "16px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span style={{ color: "#e2b148", fontSize: "16px" }}>
                          ₹ {payableAmount[index]}{" "}
                        </span>
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#999",
                            fontSize: "16px",
                          }}
                        >
                          ₹ {originalPrice}
                          {tooltipContent && (
                            <Popover
                              content={tooltipContent}
                              title={null}
                              trigger="hover"
                              placement="top"
                            >
                              <Button
                                type="text"
                                icon={<InfoCircleOutlined />}
                                style={{
                                  color: "#d97706",
                                  padding: "0",
                                  height: "auto",
                                  border: "none",
                                  fontSize: "12px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              />
                            </Popover>
                          )}
                        </span>
                        (inclusive of all taxes)
                      </span>
                    );
                  }

                  // Age-based schedules quote one fare per slab instead of a
                  // single figure — the API total assumes an adult.
                  if (fareTiers) {
                    return (
                      <FareTiers
                        tiers={fareTiers}
                        tone="gold"
                        size="sm"
                        note="Inclusive of all taxes"
                      />
                    );
                  }

                  return (
                    <>
                      <span style={{ color: "#e2b148", fontSize: "16px" }}>
                        ₹ {payableAmount[index]}
                        (inclusive of all taxes)
                      </span>
                    </>
                  );
                })()}
              </b>
            ) : (
              ""
            )}
          </div>
          <div>
            <Checkbox
              color="secondary"
              onClick={onChangeTncCheck}
              checked={tncChecks[index]}
              label={
                "I confirm that I have read and agree to the cancellation terms and the terms and conditions."
              }
            />
          </div>
          {error[index] && (
            <div
              style={{
                fontSize: "12px",
                padding: "4px 4px 4px 0",
                color: "red",
                fontWeight: "600",
                minHeight: "20px",
              }}
            >
              {error[index]}
            </div>
          )}
          <div style={{ textAlign: "right" }}>
            <Button
              size="large"
              variant="primary"
              type="primary"
              className="book-btn"
              disabled={availableSeats[index] === 0}
              style={{}}
              onClick={handleBookAction}
            >
              Book
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

JoyRideForm.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  path: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  selected: PropTypes.object.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleScheduleChange: PropTypes.func.isRequired,
  handleBookAction: PropTypes.func.isRequired,
  availableSeats: PropTypes.number.isRequired,
  payableAmount: PropTypes.number.isRequired,
  error: PropTypes.string.isRequired,
  tncChecks: PropTypes.bool.isRequired,
  onChangeTncCheck: PropTypes.func.isRequired,
  onClickTnc: PropTypes.func.isRequired,
  getSlotDescription: PropTypes.func.isRequired,
};

export default JoyRideForm;
